import { Box, Button, Flex, HStack, Spinner, Switch, Tag, Text, VStack } from '@chakra-ui/react';
import { useState } from 'react';
import { Trans } from '@lingui/react/macro';
import { djs } from '@duke-hq/djs';

import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { testIds } from './testIds';

const ONLY_MY_FEEDBACK_PREF = 'assessor:previous-feedback-only-mine';
const COLLAPSED_FEEDBACK_LENGTH = 240;

function ExpandableFeedback({ feedback }: { feedback: string }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const isTruncated = feedback.length > COLLAPSED_FEEDBACK_LENGTH;

  return (
    <>
      <Text fontSize="sm" whiteSpace="pre-wrap" mb={2}>
        {isTruncated && !isExpanded
          ? `${feedback.slice(0, COLLAPSED_FEEDBACK_LENGTH).trimEnd()}…`
          : feedback}
      </Text>

      {isTruncated ? (
        <Button
          size="xs"
          variant="link"
          colorScheme="purple"
          mb={2}
          onClick={() => setIsExpanded((expanded) => !expanded)}
        >
          {isExpanded ? <Trans>Show less</Trans> : <Trans>Show more</Trans>}
        </Button>
      ) : null}
    </>
  );
}

function readOnlyMine() {
  try {
    return localStorage.getItem(ONLY_MY_FEEDBACK_PREF) === 'true';
  } catch {
    return false;
  }
}

export function PreviousFeedbackPanel({
  questionId,
  answerId,
  unitAssessmentEnrolmentId,
  onEditAndInsert,
}: {
  questionId: string;
  answerId: number;
  unitAssessmentEnrolmentId: string;
  onEditAndInsert: (text: string) => void;
}) {
  const [onlyMine, setOnlyMine] = useState(readOnlyMine);

  const handleOnlyMineChange = (value: boolean) => {
    setOnlyMine(value);
    try {
      localStorage.setItem(ONLY_MY_FEEDBACK_PREF, value ? 'true' : 'false');
    } catch {
      // ignore any ls failures
    }
  };

  const { data, isLoading } = trpcAdmin.assessments.getPreviousFeedbackForQuestion.useQuery({
    questionId,
    answerId,
    unitAssessmentEnrolmentId,
    onlyMine,
  });

  return (
    <Box bg="bg.surface" px={6} py={5} data-testid={testIds.previousFeedbackPanel}>
      <Flex justifyContent="space-between" alignItems="center" mb={3}>
        <Text fontSize="sm" fontWeight="medium">
          <Trans>Moderation of feedback used</Trans>
        </Text>
        <HStack gap={2}>
          <Text fontSize="sm">
            <Trans>Only mine</Trans>
          </Text>
          <Switch
            isChecked={onlyMine}
            onChange={(event) => handleOnlyMineChange(event.target.checked)}
            size="sm"
          />
        </HStack>
      </Flex>

      {isLoading ? (
        <Spinner size="sm" />
      ) : data != null && data.length > 0 ? (
        <VStack gap={3} alignItems="stretch">
          {data.map((item) => (
            <Box
              key={`${item.authorName}|${item.feedback}`}
              data-testid={testIds.previousFeedbackCard}
              borderWidth="1px"
              borderColor="border.emphasis"
              borderRadius="md"
              p={3}
            >
              <Flex justifyContent="space-between" alignItems="center" mb={2}>
                <Text fontSize="sm" fontWeight="medium" textTransform="capitalize">
                  {item.authorName}
                </Text>
                <HStack gap={2}>
                  <Tag size="sm" colorScheme="gray">
                    <Trans>USED {item.usedCount}×</Trans>
                  </Tag>
                  <Text fontSize="xs" color="fg.muted">
                    {djs(item.latestAt).format('DD/MM/YY')}
                  </Text>
                </HStack>
              </Flex>
              <ExpandableFeedback feedback={item.feedback} />
              <Button
                data-testid={testIds.editAndInsertButton}
                size="xs"
                variant="outline"
                onClick={() => onEditAndInsert(item.feedback)}
              >
                <Trans>Edit & insert</Trans>
              </Button>
            </Box>
          ))}
        </VStack>
      ) : (
        <Text data-testid={testIds.emptyState} fontSize="sm" color="fg.muted">
          <Trans>No previous feedback yet</Trans>
        </Text>
      )}
    </Box>
  );
}
